import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Chip, Collapse, IconButton, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ActionBadge from './ActionBadge';

const STATUS_COLOR = {
  open:         { color: '#9ef7c5', bg: 'rgba(158,247,197,0.12)' },
  confirmed:    { color: '#9ef7c5', bg: 'rgba(158,247,197,0.14)' },
  failed:       { color: '#ff4d6a', bg: 'rgba(255,77,106,0.12)'  },
  evaluating:   { color: '#ffe55c', bg: 'rgba(255,229,92,0.12)'  },
  inconclusive: { color: '#ff9a3c', bg: 'rgba(255,154,60,0.12)'  },
  invalidated:  { color: '#9ea4d8', bg: 'rgba(158,164,216,0.10)' },
  expired:      { color: '#9ea4d8', bg: 'rgba(158,164,216,0.10)' },
};

const DIRECTION_COLOR = {
  bullish: '#9ef7c5',
  bearish: '#ff4d6a',
  neutral: '#ffe55c',
};

function fmt2(v) { return typeof v === 'number' ? v.toFixed(2) : '—'; }
function fmtPct(v) { return typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '—'; }
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return String(s); }
}

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.92)',
    overflow: 'hidden',
    marginBottom: 10,
  },
  header: {
    padding: '10px 14px',
    background: 'linear-gradient(90deg, rgba(77,121,255,0.18), rgba(10,14,30,0.96))',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  meta: {
    fontSize: 11,
    color: '#9ea4d8',
  },
  body: {
    padding: '10px 14px 12px',
  },
  reason: {
    fontSize: 12,
    lineHeight: 1.55,
    color: '#e8f5ff',
    opacity: 0.92,
    marginBottom: 8,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: 6,
    marginTop: 6,
  },
  statBox: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    padding: '6px 10px',
    border: '1px solid rgba(255,255,255,0.07)',
  },
  statLabel: {
    fontSize: 10,
    color: '#9ea4d8',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  statValue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e5e7ff',
  },
  outcomeBox: {
    marginTop: 10,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid rgba(77,121,255,0.22)',
    background: 'rgba(5,8,20,0.6)',
  },
  outcomeTitle: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 900,
    color: '#a78bfa',
    marginBottom: 6,
  },
}));

function OutcomePanel({ outcome }) {
  const classes = useStyles();
  if (!outcome) return null;
  const confirmed = Number(outcome.thesis_confirmed) === 1;
  return (
    <div className={classes.outcomeBox}>
      <Typography className={classes.outcomeTitle}>Evaluated Outcome</Typography>
      <div className={classes.statGrid}>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Result</div>
          <div className={classes.statValue} style={{ color: confirmed ? '#9ef7c5' : '#ff4d6a' }}>
            {confirmed ? 'Confirmed' : 'Failed'}
          </div>
        </div>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Outcome Score</div>
          <div className={classes.statValue}>{fmt2(Number(outcome.outcome_score))}</div>
        </div>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Confirmation φ</div>
          <div className={classes.statValue}>{fmt2(Number(outcome.confirmation_score))}</div>
        </div>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Price Return</div>
          <div className={classes.statValue} style={{ color: Number(outcome.price_return) >= 0 ? '#9ef7c5' : '#ff4d6a' }}>
            {fmtPct(Number(outcome.price_return))}
          </div>
        </div>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Max Drawdown</div>
          <div className={classes.statValue} style={{ color: '#ff9a3c' }}>
            {fmtPct(Number(outcome.max_drawdown))}
          </div>
        </div>
        <div className={classes.statBox}>
          <div className={classes.statLabel}>Evaluated</div>
          <div className={classes.statValue} style={{ fontSize: 11 }}>{fmtDate(outcome.ts_evaluated)}</div>
        </div>
      </div>
    </div>
  );
}

export default function ActionCard({ action, outcome }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const statusMeta = STATUS_COLOR[action.status] || STATUS_COLOR.expired;
  const dirColor = DIRECTION_COLOR[action.expected_direction] || '#cfd8ff';
  const qa = Number(action.action_score);
  const risk = Number(action.risk_score);

  return (
    <div className={classes.card}>
      <div
        className={classes.header}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v); }}
      >
        <div className={classes.chips}>
          <ActionBadge actionType={action.action_type} />
          <Chip
            size="small"
            label={action.status}
            style={{
              height: 18, fontSize: 9, fontWeight: 900, textTransform: 'uppercase',
              color: statusMeta.color, backgroundColor: statusMeta.bg,
              border: `1px solid ${statusMeta.color}`,
            }}
          />
          <Tooltip title="Expected direction">
            <Chip
              size="small"
              label={action.expected_direction}
              style={{
                height: 18, fontSize: 9, fontWeight: 900, textTransform: 'uppercase',
                color: dirColor, backgroundColor: 'rgba(255,255,255,0.06)',
                border: `1px solid ${dirColor}`,
              }}
            />
          </Tooltip>
          <Typography className={classes.meta}>
            {`Q_A ${fmt2(qa)} · Risk ${fmt2(risk)}`}
          </Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography className={classes.meta}>{fmtDate(action.ts_created)}</Typography>
          <IconButton size="small" style={{ padding: 2, color: '#cfd3ff' }}>
            {expanded ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}
          </IconButton>
        </div>
      </div>

      <Collapse in={expanded}>
        <div className={classes.body}>
          {action.trigger_reason ? (
            <Typography className={classes.reason}>{action.trigger_reason}</Typography>
          ) : null}
          <div className={classes.statGrid}>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Token</div>
              <div className={classes.statValue}>{action.token_symbol || '—'}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Horizon</div>
              <div className={classes.statValue}>{action.expected_horizon ? `${action.expected_horizon}h` : '—'}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Confidence</div>
              <div className={classes.statValue}>{fmt2(Number(action.confidence))}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>O_total</div>
              <div className={classes.statValue}>{fmt2(Number(action.o_total))}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>n_supporting</div>
              <div className={classes.statValue}>{action.n_supporting ?? '—'}</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Trigger State</div>
              <div className={classes.statValue} style={{ fontSize: 11 }}>{action.trigger_state || '—'}</div>
            </div>
          </div>
          <OutcomePanel outcome={outcome} />
        </div>
      </Collapse>
    </div>
  );
}
